import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, FileText, Link as LinkIcon, Copy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ProductTemplatesTab({ getAuthHeaders, categories, products }) {
  const [templates, setTemplates] = useState([]);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    category_id: '',
    product_id: '',
    specifications: '',
    quantity: '',
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/product-templates`, getAuthHeaders());
      setTemplates(response.data);
    } catch (error) {
      toast.error('Eroare la încărcarea template-urilor');
    }
  };

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        category_id: template.category_id,
        product_id: template.product_id,
        specifications: template.specifications,
        quantity: template.quantity,
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({
        category_id: '',
        product_id: '',
        specifications: '',
        quantity: '',
      });
    }
    setTemplateDialog(true);
  };

  const handleSaveTemplate = async () => {
    try {
      if (editingTemplate) {
        await axios.put(
          `${API}/admin/product-templates/${editingTemplate.id}`,
          templateForm,
          getAuthHeaders()
        );
        toast.success('Template actualizat cu succes');
      } else {
        await axios.post(`${API}/admin/product-templates`, templateForm, getAuthHeaders());
        toast.success('Template creat cu succes');
      }
      setTemplateDialog(false);
      fetchTemplates();
    } catch (error) {
      toast.error('Eroare la salvarea template-ului');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Sigur doriți să ștergeți acest template?')) return;
    try {
      await axios.delete(`${API}/admin/product-templates/${id}`, getAuthHeaders());
      toast.success('Template șters');
      fetchTemplates();
    } catch (error) {
      toast.error('Eroare la ștergerea template-ului');
    }
  };

  const copyUrlToClipboard = (template) => {
    const url = `${window.location.origin}/${template.category_slug}/${template.product_slug}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copiat în clipboard!');
  };

  const getFilteredProducts = (categoryId) => {
    return products.filter((p) => p.category_id === categoryId);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Specificațiile Noastre ({templates.length})
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Template-uri cu specificații pre-completate pentru ofertanți
          </p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          data-testid="add-template-button"
          style={{ backgroundColor: '#1a73e8' }}
        >
          <Plus className="mr-2 h-4 w-4" />
          Adaugă Template
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Categorie</TableHead>
            <TableHead>Produs</TableHead>
            <TableHead>Specificații</TableHead>
            <TableHead>Cantitate</TableHead>
            <TableHead>URL Personalizat</TableHead>
            <TableHead className="text-right">Acțiuni</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {templates.map((tmpl) => (
            <TableRow key={tmpl.id}>
              <TableCell className="font-medium">{tmpl.category_name}</TableCell>
              <TableCell>{tmpl.product_name}</TableCell>
              <TableCell className="text-sm text-gray-500">
                {tmpl.specifications.substring(0, 50)}...
              </TableCell>
              <TableCell>{tmpl.quantity}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                    /{tmpl.category_slug}/{tmpl.product_slug}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyUrlToClipboard(tmpl)}
                    data-testid={`copy-url-${tmpl.id}`}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenDialog(tmpl)}
                    data-testid={`edit-template-${tmpl.id}`}
                  >
                    <Edit className="h-4 w-4 text-blue-500" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteTemplate(tmpl.id)}
                    data-testid={`delete-template-${tmpl.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Template Dialog */}
      <Dialog open={templateDialog} onOpenChange={setTemplateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Editează Template' : 'Template Nou'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categorie</Label>
                <select
                  className="w-full border border-gray-300 rounded-md p-2"
                  value={templateForm.category_id}
                  onChange={(e) => {
                    setTemplateForm({ ...templateForm, category_id: e.target.value, product_id: '' });
                  }}
                  data-testid="template-category-select"
                >
                  <option value="">Selectați categoria</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Produs</Label>
                <select
                  className="w-full border border-gray-300 rounded-md p-2"
                  value={templateForm.product_id}
                  onChange={(e) => setTemplateForm({ ...templateForm, product_id: e.target.value })}
                  disabled={!templateForm.category_id}
                  data-testid="template-product-select"
                >
                  <option value="">Selectați produsul</option>
                  {getFilteredProducts(templateForm.category_id).map((prod) => (
                    <option key={prod.id} value={prod.id}>
                      {prod.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>Specificații Produs</Label>
              <Textarea
                value={templateForm.specifications}
                onChange={(e) => setTemplateForm({ ...templateForm, specifications: e.target.value })}
                placeholder="Descrieți specificațiile produsului..."
                className="min-h-32"
                data-testid="template-specifications-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Aceste specificații vor pre-completa formularul când ofertantul accesează link-ul
              </p>
            </div>

            <div>
              <Label>Cantitate Minimă (MOQ)</Label>
              <Input
                value={templateForm.quantity}
                onChange={(e) => setTemplateForm({ ...templateForm, quantity: e.target.value })}
                placeholder="ex: 100 bucăți"
                data-testid="template-quantity-input"
              />
            </div>

            <div className="pt-4 bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-800">
                <strong>Notă:</strong> După salvare, veți primi un URL personalizat care pre-completează
                formularul cu aceste specificații. Ofertantul va trebui să completeze doar prețul și
                datele sale de contact.
              </p>
            </div>

            <Button
              onClick={handleSaveTemplate}
              className="w-full"
              data-testid="save-template-button"
            >
              {editingTemplate ? 'Actualizează' : 'Salvează'} Template
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProductTemplatesTab;